import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import ConfettiEffect from '../components/ConfettiEffect'
import OnboardingLayout from '../components/OnboardingLayout'
import { useAuth } from '../hooks/useAuth'

const STORAGE_KEY = 'onboarding:intake'

const interestOptions = [
  {
    value: 'Entrepreneur',
    title: 'Entrepreneur',
    description: 'Building the next big thing'
  },
  {
    value: 'Cybersecurity Professional',
    title: 'Cybersecurity Professional',
    description: 'Protecting digital assets'
  },
  {
    value: 'Website Designer',
    title: 'Website Designer',
    description: 'Crafting beautiful web experiences'
  },
  {
    value: 'AI Engineer',
    title: 'AI Engineer',
    description: 'Building intelligent systems'
  },
  {
    value: 'Indie Hacker',
    title: 'Indie Hacker',
    description: 'Shipping fast and learning by doing'
  }
]

const goalOptions = [
  {
    value: 'career-transition',
    label: 'Career transition into tech',
    description: 'Break in and land a role'
  },
  {
    value: 'level-up',
    label: 'Level up my current skills',
    description: 'Get sharper, faster, and more confident'
  },
  {
    value: 'build-startup',
    label: 'Build a startup/side project',
    description: 'Validate ideas and ship products'
  },
  {
    value: 'personal-growth',
    label: 'Learn for personal growth',
    description: 'Stay curious and expand your toolkit'
  }
]

type PendingIntake = {
  interests: string[]
  learningGoals: string[]
}

function normalizeGoals(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return [value.trim()]
  }
  return []
}

function readPendingIntake(): PendingIntake | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PendingIntake>
    return {
      interests: Array.isArray(parsed.interests) ? parsed.interests : [],
      learningGoals: normalizeGoals(parsed.learningGoals),
    }
  } catch {
    return null
  }
}

export const Route = createFileRoute('/onboarding/step-1')({
  component: OnboardingStep1,
})

function OnboardingStep1() {
  const navigate = useNavigate()
  const { isAuthenticated, loading } = useAuth()
  const pending = useMemo(() => readPendingIntake(), [])
  const [selectedInterests, setSelectedInterests] = useState<string[]>(pending?.interests ?? [])
  const [selectedGoals, setSelectedGoals] = useState<string[]>(pending?.learningGoals ?? [])

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate({ to: '/signin', replace: true })
    }
  }, [isAuthenticated, loading, navigate])

  const toggleInterest = (value: string) => {
    setSelectedInterests((prev) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value)
      }
      return [...prev, value]
    })
  }

  const toggleGoal = (value: string) => {
    setSelectedGoals((prev) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value)
      }
      return [...prev, value]
    })
  }

  const handleContinue = () => {
    if (!selectedInterests.length || !selectedGoals.length) return
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ interests: selectedInterests, learningGoals: selectedGoals })
      )
    }
    navigate({ to: '/onboarding/step-2' })
  }

  const handleSkip = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY)
    }
    navigate({ to: '/courses', search: { newUser: 'true' }, replace: true })
  }

  const canContinue = selectedInterests.length > 0 && selectedGoals.length > 0

  return (
    <>
      <ConfettiEffect />
      <OnboardingLayout
        step={1}
        title="Welcome to TechNYC.io! Let's personalize your experience"
        subtitle="Tell us what you're building so we can curate the right courses, labs, and community moments for you."
        onSkip={handleSkip}
      >
        <h2 className="onboarding-section-header">What describes you?</h2>
        <motion.section
          className="preference-grid"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.5 } }}
        >
          {interestOptions.map((option) => {
            const isSelected = selectedInterests.includes(option.value)
            return (
              <motion.button
                type="button"
                key={option.value}
                className={`preference-card ${isSelected ? 'selected' : ''}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => toggleInterest(option.value)}
              >
                <h3>{option.title}</h3>
                <p>{option.description}</p>
              </motion.button>
            )
          })}
        </motion.section>

        <h2 className="onboarding-section-header">What's your learning goal?</h2>
        <motion.section
          className="goal-options"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.1, duration: 0.5 } }}
        >
          {goalOptions.map((goal) => {
            const isSelected = selectedGoals.includes(goal.value)
            return (
              <label key={goal.value} className={`goal-option ${isSelected ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  name="learning-goal"
                  value={goal.value}
                  checked={isSelected}
                  onChange={() => toggleGoal(goal.value)}
                />
                <div>
                  <span>{goal.label}</span>
                  <small>{goal.description}</small>
                </div>
              </label>
            )
          })}
        </motion.section>

        <div className="onboarding-actions">
          <button className="onboarding-primary-btn" onClick={handleContinue} disabled={!canContinue}>
            Continue
          </button>
          <button className="onboarding-secondary-btn" type="button" onClick={handleSkip}>
            I'll explore first
          </button>
        </div>
      </OnboardingLayout>
    </>
  )
}
